import { useEffect, useMemo } from "react"
import { observer } from "mobx-react-lite"
import { makeAutoObservable } from "mobx"
import ReactSlider from "react-slider"
import { formatCurrency } from "@cruisen/i18n"
import styles from "./styles.module.scss"

class RangeSliderStore {
  minValueLabel = 0
  maxValueLabel = 0

  constructor(initialData: { min: number; max: number }) {
    this.minValueLabel = initialData.min
    this.maxValueLabel = initialData.max
    makeAutoObservable(this)
  }

  changeFilterLabels(low: number, high: number) {
    this.minValueLabel = low
    this.maxValueLabel = high
  }
}

export const RangeSlider = observer(
  ({
    range,
    values,
    priceSliderStep,
    isSliderDisabled = false,
    valueType = "default",
    onValuesChangeFinish,
    setIsDragging,
  }: {
    range: { min: number; max: number }
    values: { min: number; max: number }
    priceSliderStep: number
    isSliderDisabled?: boolean
    valueType?: "price" | "default"
    onValuesChangeFinish: (min: number, max: number) => void
    setIsDragging?: (value: boolean) => void
  }) => {
    const state = useMemo(() => new RangeSliderStore(range), [])

    useEffect(() => {
      if (values.min === null || values.max === null) return
      state.changeFilterLabels(values.min, values.max)
    }, [values.min, values.max])

    const handleChange = ([min, max]) => {
      state.changeFilterLabels(min, max)
    }

    const handleAfterChange = ([min, max]) => {
      onValuesChangeFinish(min, max)
      setTimeout(() => {
        setIsDragging?.(false)
      }, 100)
    }

    return (
      <div className={styles.rangeSliderContainer}>
        <div className={styles.sliderWrapper}>
          <ReactSlider
            className={styles.slider}
            thumbClassName={styles.thumb}
            trackClassName={styles.track}
            min={range.min}
            max={range.max}
            minDistance={priceSliderStep}
            step={priceSliderStep}
            value={[state.minValueLabel, state.maxValueLabel]}
            disabled={isSliderDisabled}
            onChange={handleChange}
            onAfterChange={handleAfterChange}
            onBeforeChange={() => {
              setIsDragging?.(true)
            }}
          />
        </div>
        <div className={styles.sliderValues}>
          <p>{valueType === "price" ? formatCurrency(state.minValueLabel) : state.minValueLabel}</p>
          <p>{valueType === "price" ? formatCurrency(state.maxValueLabel) : state.maxValueLabel}</p>
        </div>
      </div>
    )
  },
)
